import { Flex, Menu, MenuItem, MenuList, Text } from "@chakra-ui/react";
import { EditorState, Modifier, SelectionState } from "draft-js";
import React from "react";
import { HiOutlineChevronDown } from "react-icons/hi";
import { MdCode } from "react-icons/md";

import { Button, MenuButton } from "../../../../components";
import { formatWildcard } from "./constants";

export interface Wildcard {
  name: string;
  label: string;
}

export interface WildcardsDropdownProps {
  editorState: EditorState;
  onChange: (editorState: EditorState) => void;
  wildcards: Wildcard[];
  lastSelectionRef: React.MutableRefObject<SelectionState | null>;
}

const WildcardsDropdown: React.FC<WildcardsDropdownProps> = ({
  editorState,
  onChange,
  wildcards,
  lastSelectionRef,
}) => {
  const handleInsertWildcard = (wildcardName: string) => {
    const contentState = editorState.getCurrentContent();
    let selection = editorState.getSelection();

    // Use the last saved selection if the editor doesn't have focus
    if (!selection.getHasFocus() && lastSelectionRef.current) {
      selection = lastSelectionRef.current;
    }

    // Insert the wildcard text at the selection position
    const wildcardText = formatWildcard(wildcardName);
    const newContentState = Modifier.insertText(
      contentState,
      selection,
      wildcardText
    );

    // Update editor state with new content
    const newEditorState = EditorState.push(
      editorState,
      newContentState,
      "insert-characters"
    );

    // Move cursor to after the inserted wildcard
    const newSelection = newEditorState.getSelection().merge({
      anchorOffset: selection.getAnchorOffset() + wildcardText.length,
      focusOffset: selection.getFocusOffset() + wildcardText.length,
    });

    const finalEditorState = EditorState.forceSelection(
      newEditorState,
      newSelection
    );

    onChange(finalEditorState);
  };

  return (
    <Menu>
      <MenuButton
        as={Button}
        leftIcon={<MdCode size={20} />}
        rightIcon={<HiOutlineChevronDown />}
        size="sm"
        variant="outline"
        colorScheme="blue"
      >
        Wildcards
      </MenuButton>
      <MenuList bg="white" zIndex={1000}>
        {wildcards.map((wildcard) => (
          <MenuItem
            key={wildcard.name}
            onClick={() => handleInsertWildcard(wildcard.name)}
          >
            <Flex alignItems="center">
              <Text fontFamily="monospace" fontSize="sm" color="blue.700">
                {formatWildcard(wildcard.name)}
              </Text>
            </Flex>
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};

export default WildcardsDropdown;
